import { useContext, useState } from "react";
import { t } from "i18next";
import { BsStars } from "react-icons/bs";
import { HiOutlinePlus } from "react-icons/hi2";
import { TbTrash } from "react-icons/tb";
import { Button, Dropdown, Tooltip } from "components/ui";
import capitalize from "components/ui/utils/capitalize";
import { CompanyContext } from "contexts/CompanyContext";
import MySocialIcon from "components/shared/MySocialIcon";
import { Loading } from "components/shared";
import usePostGenerator from "utils/hooks/usePostGenerator";
import { socialNetworks } from "views/calendar/SocialNetworkSidePanel";
import { getSocialNetworkIntegration } from "utils/getSocialNetworkIntegration";
import { allowedCommentsNetworks } from "utils/comments";

/**
 * Pill-style network selector for the composer. Same data and callbacks as
 * the legacy PostArticleSocials strip (select network, AI-reformat, delete,
 * create-for-network by copying the first post), restyled as pills with
 * per-network validation/publish-error dots.
 */
const ComposerNetworkPills = ({
    posts,
    postGroup,
    activeSocialNetwork,
    setActiveSocialNetwork,
    isEditable = true,
    validationErrors,
    onCreateNetworkPost,
    onDeleteNetworkPost,
    onReplaceNetworkPost
}) => {

    const { companySocials } = useContext(CompanyContext);
    const { generateFromOtherPost, copyFromOtherPost } = usePostGenerator();
    const [reformattingNetwork, setReformattingNetwork] = useState(undefined);

    const createdNetworks = [...new Set(posts?.map((post) => post?.network))];
    const remainingNetworks = socialNetworks
        .sort((a, b) => +(!getSocialNetworkIntegration(a, companySocials)) - +(!getSocialNetworkIntegration(b, companySocials)))
        .filter(network => !createdNetworks.includes(network));

    const onGenerateFromPost = async (network) => {
        setReformattingNetwork(network);
        try {
            const basePost = posts?.[0];
            const newPost = await generateFromOtherPost(network, basePost?.text, basePost?.assets, basePost?.preferences?.media_product_type);
            onReplaceNetworkPost(network, { ...newPost?.posts?.[0] });
        } catch (err) {
            console.error(err);
        }
        setReformattingNetwork(undefined);
    };

    const onCreateForNetwork = (network) => {
        const postToCopy = postGroup?.posts?.[0];
        if (!postToCopy) return;

        let newPost = copyFromOtherPost(
            network,
            postToCopy?.description || postGroup?.posts?.find(p => !!p?.description)?.description || undefined,
            postToCopy?.assets || postGroup?.posts?.find(p => !!p?.assets?.length)?.assets || undefined,
            postToCopy?.title || postGroup?.posts?.find(e => !!e?.title)?.title || undefined,
            postToCopy?.preferences?.media_product_type || "FEED",
            postToCopy?.cover || undefined,
            postToCopy?.preferences?.video_cover_timestamp_ms || undefined
        );
        newPost = { ...newPost?.posts?.[0] };

        if (allowedCommentsNetworks.includes(network) && allowedCommentsNetworks.includes(postToCopy?.social_network_type)) {
            newPost.comments_to_create = postToCopy?.comments_to_create;
        }

        onCreateNetworkPost(newPost);
    };

    const getNetworkErrorDot = (network) => {
        const hasPublishErrors = postGroup?.posts?.filter(p => p.social_network_type == network && p.status == "error" && !!p.status_message)?.length > 0;
        const hasValidationErrors = validationErrors?.filter(e => e?.network == network)?.length > 0;
        if (!hasPublishErrors && !hasValidationErrors) return null;
        return (
            <Tooltip
                title={hasPublishErrors
                    ? `${capitalize(network)} post has publishing errors`
                    : `${capitalize(network)} post has validation errors`
                }
                wrapperClass={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-secondary-500 dark:ring-gray-800 ${hasPublishErrors ? `bg-red-500 dark:bg-red-400` : `bg-yellow-400 dark:bg-yellow-500`}`}
            />
        );
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            {createdNetworks.map((network) => {
                const isActive = network == activeSocialNetwork;
                return (
                    <div
                        key={network}
                        onClick={() => !isActive && setActiveSocialNetwork(network)}
                        className={`relative h-9 shrink-0 rounded-full inline-flex items-center cursor-pointer transition-all ${isActive
                            ? `pl-1.5 ${isEditable ? `pr-1` : `pr-3`} gap-1.5 bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-500 dark:ring-primary-400`
                            : `px-1.5 ring-1 ring-gray-200 dark:ring-gray-600 hover:ring-gray-300 dark:hover:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50`
                        }`}
                    >
                        <span className="relative shrink-0">
                            <MySocialIcon network={network} className="!w-6 !h-6 !rounded-full shadow" wrapperClassName="!w-6 !h-6" />
                            {reformattingNetwork == network && (
                                <span className="flex items-center justify-center absolute left-0 top-0 w-full h-full rounded-full">
                                    <Loading type="element" size="24" />
                                </span>
                            )}
                        </span>
                        {isActive && (
                            <>
                                <span className="text-sm font-semibold text-tertiary-700 dark:text-gray-100">
                                    {capitalize(network)}
                                </span>
                                {isEditable && (
                                    <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title={t('composer.aiReformat', 'AI: Format post for this network')}>
                                            <Button
                                                size="xs"
                                                shape="circle"
                                                className="!bg-transparent hover:!bg-fuchsia-600/20 hover:!text-fuchsia-600 dark:hover:!bg-fuchsia-500/20 dark:hover:!text-fuchsia-400 !rounded-full"
                                                icon={<BsStars />}
                                                loading={reformattingNetwork == network}
                                                onClick={() => onGenerateFromPost(network)}
                                            />
                                        </Tooltip>
                                        <Tooltip title={t('composer.deleteNetworkPost', 'Delete post')}>
                                            <Button
                                                size="xs"
                                                shape="circle"
                                                className="!bg-transparent hover:!bg-red-600/20 hover:!text-red-600 dark:hover:!bg-red-500/20 dark:hover:!text-red-400 !rounded-full"
                                                icon={<TbTrash />}
                                                onClick={() => onDeleteNetworkPost(network)}
                                            />
                                        </Tooltip>
                                    </span>
                                )}
                            </>
                        )}
                        {getNetworkErrorDot(network)}
                    </div>
                );
            })}
            {isEditable && !!remainingNetworks?.length && (
                <Dropdown
                    placement="bottom-start"
                    parentClassName="!shrink-0"
                    menuClass="max-h-[320px] overflow-y-auto"
                    portal={true}
                    renderTitle={
                        <Tooltip title={t('composer.addNetwork', 'Add network')}>
                            <div className="h-9 w-9 shrink-0 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-tertiary-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-300 hover:border-primary-400 transition-colors flex items-center justify-center cursor-pointer">
                                <HiOutlinePlus className="text-lg" />
                            </div>
                        </Tooltip>
                    }
                >
                    {remainingNetworks.map((network) => (
                        <Dropdown.Item
                            key={network}
                            onClick={() => onCreateForNetwork(network)}
                            className="flex items-center gap-2.5 py-2 px-4"
                        >
                            <MySocialIcon
                                network={network}
                                className="!w-6 !h-6 !rounded-full"
                                wrapperClassName="!w-6 !h-6"
                            />
                            <span>{capitalize(network)}</span>
                        </Dropdown.Item>
                    ))}
                </Dropdown>
            )}
        </div>
    );
};

export default ComposerNetworkPills;
